import { createBrowserRouter, Navigate, Outlet, useSearchParams } from 'react-router';
import { PublicTheme } from '@/components/ui/PublicTheme';
import { lazy, Suspense, type ReactNode } from 'react';
import { Spin } from 'antd';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveRole, useCanAccess } from '@/hooks/useCanAccess';
import { homePathFor } from '@/lib/featureAccess';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { LazyErrorBoundary } from '@/components/shared/LazyErrorBoundary';

// Lazy load pages
const Dashboard = lazy(() => import('@/pages/DashboardV2'));
const ProfitLoss = lazy(() => import('@/pages/ProfitLoss'));
const Reports = lazy(() => import('@/pages/Reports'));
const Transactions = lazy(() => import('@/pages/Transactions'));
const Statements = lazy(() => import('@/pages/Statements'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const Employees = lazy(() => import('@/pages/Employees'));
const Timesheets = lazy(() => import('@/pages/Timesheets'));
const Billers = lazy(() => import('@/pages/Billers').then(m => ({ default: m.Billers })));
const Taxes = lazy(() => import('@/pages/Taxes'));
const AuditLog = lazy(() => import('@/pages/AuditLog'));
const Chat = lazy(() => import('@/pages/Chat'));
const Settings = lazy(() => import('@/pages/Settings'));
const Profile = lazy(() => import('@/pages/Profile'));
const ForcedPasswordChange = lazy(() => import('@/pages/ForcedPasswordChange'));
const Help = lazy(() => import('@/pages/Help'));
const Billing = lazy(() => import('@/pages/Billing'));
const BillingCheckoutSuccess = lazy(() => import('@/pages/BillingCheckoutSuccess'));
const BillingCheckoutCancel = lazy(() => import('@/pages/BillingCheckoutCancel'));
// Admin pages
const AdminBranding = lazy(() => import('@/pages/admin/Branding'));
const AdminMaintenance = lazy(() => import('@/pages/admin/Maintenance'));
const AdminSecurity = lazy(() => import('@/pages/admin/Security'));
const AdminWorkspaces = lazy(() => import('@/pages/admin/Workspaces'));
const AdminUsers = lazy(() => import('@/pages/admin/Users'));
const AdminTemplates = lazy(() => import('@/pages/admin/Templates'));
const AdminPromotions = lazy(() => import('@/pages/admin/Promotions'));
const AdminAffiliates = lazy(() => import('@/pages/admin/Affiliates'));
const AdminBilling = lazy(() => import('@/pages/admin/Billing'));

// Auth pages
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));

// Public pages
const PublicUpload = lazy(() => import('@/pages/PublicUpload'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const MobileInvoiceUpload = lazy(() => import('@/pages/MobileInvoiceUpload'));

// Layouts
const MainLayout = lazy(() => import('@/layouts/MainLayout'));

// Error pages
const NotFound = lazy(() => import('@/pages/errors/NotFound'));
const ServerError = lazy(() => import('@/pages/errors/ServerError'));
const Forbidden = lazy(() => import('@/pages/errors/Forbidden'));
const Unauthorized = lazy(() => import('@/pages/errors/Unauthorized'));
const BadRequest = lazy(() => import('@/pages/errors/BadRequest'));

function PageLoader() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Spin size="large" />
    </div>
  );
}

function SuspenseWrapper({ children }: { children: ReactNode }) {
  return (
    <LazyErrorBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </LazyErrorBoundary>
  );
}

/**
 * Protected route wrapper - requires authentication
 */
/**
 * Renders a page only if the current role can access the named capability,
 * otherwise shows /forbidden.
 *
 * The sidebar already hides links the role cannot use, but the routes had no
 * guard — so typing `/audit-log` as an employee rendered the full page chrome
 * and an error Alert from the backend's 403. The server was enforcing it
 * correctly; the client was just presenting the refusal as a malfunction.
 * `Billing` and `Settings` already do this check in-component, so this is the
 * house pattern, lifted to the route where it belongs.
 */
function RequireCapability({ capability }: { capability: Parameters<typeof useCanAccess>[0] }) {
  const allowed = useCanAccess(capability);
  return allowed ? <Outlet /> : <Forbidden />;
}

/** `/admin/*` is the platform console: the ACCOUNT flag, never a workspace role. */
function RequirePlatformAdmin() {
  const { user } = useAuth();
  return user?.isPlatformAdmin === true ? <Outlet /> : <Forbidden />;
}

function SettingsEntry() {
  const [params] = useSearchParams();
  if (params.get('tab') === 'profile') return <Navigate to="/profile" replace />;
  return <RequireCapability capability="settings" />;
}

/**
 * `/dashboard` is where every "go home" link points. A role that cannot open
 * it (an employee) is sent to its own home instead of a 403 page — otherwise
 * signing in, switching workspace and the error pages' "Go to dashboard" all
 * end on Forbidden.
 */
function DashboardHome() {
  const role = useActiveRole();
  const home = homePathFor(role);
  return home === '/dashboard' ? <Outlet /> : <Navigate to={home} replace />;
}

function ProtectedRoute() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { isLoading: workspaceLoading } = useWorkspace();

  if (authLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // uiv2 D3: an admin reset this account's password. Nothing but the change
  // screen renders — on every route, before the workspace resolves — and the
  // API refuses the rest with 403 PASSWORD_CHANGE_REQUIRED.
  if (user?.requirePasswordChange === true) {
    return (
      <SuspenseWrapper>
        <ForcedPasswordChange />
      </SuspenseWrapper>
    );
  }

  // Authenticated, but the workspace context is still resolving. Wait until it
  // finishes so the `x-workspace-id` header is synced to the API client before
  // any page mounts — otherwise the page's workspace-scoped queries fire without
  // the header and are rejected by the `requireWorkspace()` middleware. This is
  // a deterministic condition gate, replacing any fixed startup delay.
  if (workspaceLoading) {
    return <PageLoader />;
  }

  return (
    <SuspenseWrapper>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </SuspenseWrapper>
  );
}

/**
 * Auth route wrapper - redirects to dashboard if already authenticated
 */
function AuthRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <SuspenseWrapper>
      <Outlet />
    </SuspenseWrapper>
  );
}

/**
 * Landing route - shows homepage for guests, redirects to dashboard for authenticated
 */
function LandingRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <SuspenseWrapper>
      <HomePage />
    </SuspenseWrapper>
  );
}

export const router = createBrowserRouter([
  // Landing page - shows homepage for guests, dashboard for authenticated users
  {
    path: '/',
    element: <LandingRoute />,
  },

  // Public homepage (always accessible)
  {
    path: '/home',
    element: (
      <SuspenseWrapper>
        <HomePage />
      </SuspenseWrapper>
    ),
  },

  // Public upload route (no auth)
  {
    path: '/upload/:uploadKey',
    element: (
      <SuspenseWrapper>
        <PublicTheme>
          <PublicUpload />
        </PublicTheme>
      </SuspenseWrapper>
    ),
  },

  // Mobile invoice upload (requires authentication - redirects to login if not logged in)
  {
    path: '/inv',
    element: (
      <SuspenseWrapper>
        <MobileInvoiceUpload />
      </SuspenseWrapper>
    ),
  },

  // Auth routes (login, register) - also accessible from homepage
  {
    element: <AuthRoute />,
    children: [
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/register',
        element: <Register />,
      },
    ],
  },

  // Protected routes (require authentication)
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardHome />,
        children: [{ path: '/dashboard', element: <Dashboard /> }],
      },
      {
        element: <RequireCapability capability="dashboard" />,
        children: [{ path: '/pnl', element: <ProfitLoss /> }],
      },
      {
        element: <RequireCapability capability="dashboard" />,
        children: [{ path: '/reports', element: <Reports /> }],
      },
      {
        element: <RequireCapability capability="transactions" />,
        children: [
          { path: '/transactions', element: <Transactions /> },
          // uiv2 DB-15: the statements behind those transactions, same gate.
          { path: '/statements', element: <Statements /> },
        ],
      },
      {
        element: <RequireCapability capability="invoices" />,
        children: [{ path: '/invoices', element: <Invoices /> }],
      },
      {
        element: <RequireCapability capability="taxes" />,
        children: [{ path: '/taxes', element: <Taxes /> }],
      },
      {
        element: <RequireCapability capability="employees" />,
        children: [{ path: '/employees', element: <Employees /> }],
      },
      {
        path: '/timesheets',
        element: <Timesheets />,
      },
      {
        element: <RequireCapability capability="billers" />,
        children: [{ path: '/billers', element: <Billers /> }],
      },
      {
        element: <RequireCapability capability="auditLog" />,
        children: [
          {
            path: '/audit-log',
            element: <AuditLog />,
          },
        ],
      },
      {
        // Role gate, matching `requireMinRole('accountant')` on the API. Without
        // it a sub-accountant reaching /chat by URL got the full chat UI and a
        // generic "Sorry, I encountered an error" on every send, because a role
        // 403 carries no error code for the client to classify. The plan gate
        // for `aiClassification` is handled inside the page, which keeps the
        // feature visible and shows the upgrade path.
        element: <RequireCapability capability="chat" />,
        children: [
          {
            path: '/chat',
            element: <Chat />,
          },
        ],
      },
      {
        // Settings is the workspace admin's alone (access matrix, 2026-09-25).
        // A bookmark to its old profile tab goes to /profile FIRST, so a
        // non-admin following it lands on their profile, not on Forbidden.
        element: <SettingsEntry />,
        children: [{ path: '/settings', element: <Settings /> }],
      },
      {
        path: '/profile',
        element: <Profile />,
      },
      {
        path: '/help',
        element: <Help />,
      },
      {
        // SET-15: this page was linked from nowhere and could do nothing — its
        // GET 403s for any workspace but the active one, and the server refuses
        // to delete the active one. Its details now live on the Workspace tab.
        path: '/workspace/:workspaceId/settings',
        element: <Navigate to="/settings?tab=space" replace />,
      },
      {
        path: '/billing',
        element: <Billing />,
      },
      {
        path: '/billing/checkout/success',
        element: <BillingCheckoutSuccess />,
      },
      {
        path: '/billing/checkout/cancel',
        element: <BillingCheckoutCancel />,
      },
      // Admin routes - redirect /admin to first sub-section
      {
        // /admin is the platform admin's (the app developer) and nobody else's —
        // `users.is_platform_admin`, never a workspace role. It had no client
        // guard: typing /admin/branding drew the console, every call a 403.
        element: <RequirePlatformAdmin />,
        children: [
          {
            path: '/admin',
            element: <Navigate to="/admin/branding" replace />,
          },
          {
            path: '/admin/branding',
            element: <AdminBranding />,
          },
          {
            path: '/admin/maintenance',
            element: <AdminMaintenance />,
          },
          {
            path: '/admin/security',
            element: <AdminSecurity />,
          },
          {
            path: '/admin/workspaces',
            element: <AdminWorkspaces />,
          },
          {
            path: '/admin/users',
            element: <AdminUsers />,
          },
          {
            path: '/admin/templates',
            element: <AdminTemplates />,
          },
          {
            path: '/admin/promotions',
            element: <AdminPromotions />,
          },
          {
            path: '/admin/affiliates',
            element: <AdminAffiliates />,
          },
          {
            path: '/admin/billing',
            element: <AdminBilling />,
          },
        ],
      },
    ],
  },

  // Error pages (can navigate to these programmatically)
  {
    path: '/400',
    element: (
      <SuspenseWrapper>
        <BadRequest />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/401',
    element: (
      <SuspenseWrapper>
        <Unauthorized />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/403',
    element: (
      <SuspenseWrapper>
        <Forbidden />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/500',
    element: (
      <SuspenseWrapper>
        <ServerError />
      </SuspenseWrapper>
    ),
  },

  // Catch-all 404
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFound />
      </SuspenseWrapper>
    ),
  },
]);
